import React, { useState, useRef, useEffect } from 'react';
import VideoPlayer from './components/VideoPlayer';
import WarningOverlay from './components/WarningOverlay';
import CommentaryLink from './components/CommentaryLink';
import MusicSheetViewer from './components/MusicSheetViewer';
import NotesPanel from './components/NotesPanel';
import SpotlightEffect from './components/SpotlightEffect';
import VideoOverlay from './components/VideoOverlay';
import { formatTime } from './utils/timeUtils';
import spotlightEffectsData from './data/spotlightEffects.json';

function App() {
  // Filter visibility states
  const [showStoryline, setShowStoryline] = useState(true);
  const [showMusicalCounts, setShowMusicalCounts] = useState(true);
  const [showTechnicalNotes, setShowTechnicalNotes] = useState(true);
  const [showReference, setShowReference] = useState(true);
  
  // Display location states (video vs panel)
  const [storylineOnVideo, setStorylineOnVideo] = useState(false);
  const [musicalCountsOnVideo, setMusicalCountsOnVideo] = useState(false);
  const [technicalNotesOnVideo, setTechnicalNotesOnVideo] = useState(false);
  
  // Text size state (default: 80%)
  const [textSize, setTextSize] = useState(80);
  
  // Apply text size to body element
  useEffect(() => {
    document.body.setAttribute('data-text-size', textSize);
  }, [textSize]);
  
  const [showMusicSheet, setShowMusicSheet] = useState(false);
  const [showOverlays, setShowOverlays] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [isCustomFullScreen, setIsCustomFullScreen] = useState(false);
  const videoControlsRef = useRef(null);
  const appContainerRef = useRef(null);
  
  // Function to handle time updates from the video player
  const handleTimeUpdate = (time) => {
    setCurrentTime(time);
  };
  
  // Function to get video controls reference
  const handleVideoRef = (controls) => {
    videoControlsRef.current = controls;
  };
  
  // Function to toggle custom full screen mode
  const toggleCustomFullScreen = () => {
    setIsCustomFullScreen(!isCustomFullScreen);
  };
  
  // Handle escape key to exit custom full screen mode
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isCustomFullScreen) {
        setIsCustomFullScreen(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isCustomFullScreen]);

  return (
    <div 
      ref={appContainerRef}
      className={`${isCustomFullScreen ? 'fixed inset-0 z-50 bg-black' : 'min-h-screen py-8'}`}
    >
      <div className={`${isCustomFullScreen ? 'h-full' : 'container mx-auto px-4'}`}>
        {!isCustomFullScreen && (
          <h1 className="text-3xl font-bold text-center mb-8 text-white">
            Lady of the Camellias - Interactive Video
          </h1>
        )}
        
        <div className={`${isCustomFullScreen ? 'h-full' : 'flex flex-col md:flex-row gap-4'}`}>
          {/* Video container */}
          <div className={`${isCustomFullScreen ? 'h-full' : 'md:w-2/3'}`}>
            {/* Filter buttons above video */}
            <div className={`mb-2 bg-gray-800 p-3 ${isCustomFullScreen ? '' : 'rounded-lg'} shadow-md`}>
              <div className="flex flex-wrap gap-2 justify-center">
                {/* Text size slider */}
                <div className="filter-group w-full mb-2">
                  <div className="flex items-center justify-between">
                    <label className="text-white text-sm mr-2">Text Size:</label>
                    <div className="flex items-center flex-1 max-w-xs">
                      <span className="text-white text-xs mr-2">A</span>
                      <input 
                        type="range" 
                        min="5" 
                        max="100" 
                        value={textSize} 
                        onChange={(e) => setTextSize(parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                      />
                      <span className="text-white text-sm ml-2">A</span>
                      <span className="text-white text-xs ml-2">{textSize}%</span>
                    </div>
                  </div>
                </div>
                {/* Storyline filter */}
                <div className="filter-group flex items-center space-x-1">
                  <button 
                    className={`filter-button px-3 py-1 rounded text-sm ${showStoryline ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'}`}
                    onClick={() => setShowStoryline(!showStoryline)}
                  >
                    Storyline
                  </button>
                  {showStoryline && (
                    <button 
                      className={`display-toggle px-2 py-1 rounded text-xs ${storylineOnVideo ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'}`}
                      onClick={() => setStorylineOnVideo(!storylineOnVideo)}
                      title={storylineOnVideo ? "Show in panel" : "Show on video"}
                    >
                      {storylineOnVideo ? "On Video" : "In Panel"}
                    </button>
                  )}
                </div>
                
                {/* Technical Notes filter */}
                <div className="filter-group flex items-center space-x-1">
                  <button 
                    className={`filter-button px-3 py-1 rounded text-sm ${showTechnicalNotes ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'}`}
                    onClick={() => setShowTechnicalNotes(!showTechnicalNotes)}
                  >
                    Technical
                  </button>
                  {showTechnicalNotes && (
                    <button 
                      className={`display-toggle px-2 py-1 rounded text-xs ${technicalNotesOnVideo ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'}`}
                      onClick={() => setTechnicalNotesOnVideo(!technicalNotesOnVideo)}
                      title={technicalNotesOnVideo ? "Show in panel" : "Show on video"}
                    >
                      {technicalNotesOnVideo ? "On Video" : "In Panel"}
                    </button>
                  )}
                </div>
                
                {/* Music filter */}
                <div className="filter-group flex items-center space-x-1">
                  <button 
                    className={`filter-button px-3 py-1 rounded text-sm ${showMusicalCounts ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'}`}
                    onClick={() => setShowMusicalCounts(!showMusicalCounts)}
                  >
                    Music
                  </button>
                  {showMusicalCounts && (
                    <button 
                      className={`display-toggle px-2 py-1 rounded text-xs ${musicalCountsOnVideo ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'}`}
                      onClick={() => setMusicalCountsOnVideo(!musicalCountsOnVideo)}
                      title={musicalCountsOnVideo ? "Show in panel" : "Show on video"}
                    >
                      {musicalCountsOnVideo ? "On Video" : "In Panel"}
                    </button>
                  )}
                </div>
                
                {/* Reference filter (always in panel) */}
                <div className="filter-group">
                  <button 
                    className={`filter-button px-3 py-1 rounded text-sm ${showReference ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'}`}
                    onClick={() => setShowReference(!showReference)}
                  >
                    Reference
                  </button>
                </div>
                
                {/* Toggle button for overlays */}
                <div className="filter-group">
                  <button 
                    className={`filter-button px-3 py-1 rounded text-sm ${showOverlays ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'}`}
                    onClick={() => setShowOverlays(!showOverlays)}
                  >
                    {showOverlays ? 'Hide Overlays' : 'Show Overlays'}
                  </button>
                </div>
                
                {/* Custom full screen toggle button */}
                <div className="filter-group">
                  <button 
                    className={`filter-button px-3 py-1 rounded text-sm ${isCustomFullScreen ? 'bg-red-600 text-white' : 'bg-gray-700 text-gray-300'}`}
                    onClick={toggleCustomFullScreen}
                  >
                    {isCustomFullScreen ? 'Exit Full Screen' : 'Full Screen'}
                  </button>
                </div>
              </div>
            </div>
            
            <div className={`video-container bg-black ${isCustomFullScreen ? '' : 'rounded-lg'} overflow-hidden shadow-xl relative ${isCustomFullScreen ? 'h-[calc(100%-48px)]' : ''}`}>
              <VideoPlayer 
                cloudflareStream={true}
                streamSrc="/cloudflare-player.html"
                onTimeUpdate={handleTimeUpdate}
                onRef={handleVideoRef}
                isCustomFullScreen={isCustomFullScreen}
                fallbackMessage={
                  <div className="text-white text-center p-4">
                    <p className="text-xl mb-2">Video loading error</p>
                    <p>The video could not be loaded. This may be due to network issues or the video being unavailable.</p>
                  </div>
                }
              />
              
              
              {/* Interactive elements */}
              {showOverlays && (
                <>
                {/* Video overlays for notes */}
                <VideoOverlay
                  currentTime={currentTime}
                  showStoryline={showStoryline}
                  showMusicalCounts={showMusicalCounts}
                  showTechnicalNotes={showTechnicalNotes}
                  storylineOnVideo={storylineOnVideo}
                  musicalCountsOnVideo={musicalCountsOnVideo}
                  technicalNotesOnVideo={technicalNotesOnVideo}
                  videoControls={videoControlsRef.current}
                  textSize={textSize}
                />
                
                {/* Spotlight effects from JSON data */}
                {spotlightEffectsData.map((effect, index) => (
                  <SpotlightEffect 
                    key={index}
                    timestamp={effect.timestamp} 
                    seconds={effect.seconds} 
                    currentTime={currentTime} 
                    position={effect.position}
                    duration={effect.duration}
                    effectType={effect.effectType || 'spotlight'}
                    modalContent={effect.modalContent}
                    videoControls={videoControlsRef.current}
                  />
                ))}
                  
                  {/* Warning overlay for dangerous lifts */}
                  <WarningOverlay currentTime={currentTime} />
                  
                  {/* Commentary link */}
                  <CommentaryLink videoControls={videoControlsRef.current} />
                </>
              )}
            </div>
            
            {showMusicSheet && <MusicSheetViewer currentTime={currentTime} />}
          </div>
          
          {/* Notes panel - only show when not in custom full screen */}
          {!isCustomFullScreen && (
            <div className="md:w-1/3 mt-4 md:mt-0">
              <NotesPanel 
                currentTime={currentTime}
                showStoryline={showStoryline}
                showMusicalCounts={showMusicalCounts}
                showTechnicalNotes={showTechnicalNotes}
                showReference={showReference}
                storylineOnVideo={storylineOnVideo}
                musicalCountsOnVideo={musicalCountsOnVideo}
                technicalNotesOnVideo={technicalNotesOnVideo}
                videoControls={videoControlsRef.current}
                textSize={textSize}
              />
            </div>
          )}
        </div>
        
        {!isCustomFullScreen && (
          <div className="mt-8 bg-gray-800 p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-4 text-white">About This Interactive Video</h2>
            <p className="text-gray-300">
              This interactive video player allows you to view "Lady of the Camellias - Purple pas de deux" 
              with additional context. Use the filter buttons to toggle between storyline, musical counts, and technical notes.
              Watch for special interactive elements during key moments of the performance. You can also view the music sheet
              to follow along with the performance.
            </p>
            
            <div className="mt-4 flex items-center">
              <button 
                className={`mr-4 px-4 py-2 rounded ${showMusicSheet ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'}`}
                onClick={() => setShowMusicSheet(!showMusicSheet)}
              >
                {showMusicSheet ? 'Hide Music Sheet' : 'Show Music Sheet'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
